import React, { useEffect, useRef, useState } from 'react'
import { Button } from '@mui/material'
import { Helmet } from 'react-helmet'
import { useNavigate, useLocation } from 'react-router-dom'
import MenuScanLoadingScreen from '../../components/ui/MenuScanLoadingScreen'
import { ROUTES } from '../../helpers/routes-helper'
import { MENU_SCAN_STATUS, type MenuScanResult } from '../../api/menuScanApi'
import { errorLog } from '../../utils/log-helper'
import { useAppDispatch, useAppSelector } from '../../store'
import { getMenuScanStatusThunk, selectMenuScanUpload } from '../../store/menuScanSlice'

const POLL_INTERVAL_MS = 2000

const MenuScanLoading: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { uploading, error: uploadError } = useAppSelector(selectMenuScanUpload)
  const pageState = (location.state as { scanId?: string; uploading?: boolean } | undefined)
  const scanId = pageState?.scanId
  const uploadingFromState = !!pageState?.uploading
  const [result, setResult] = useState<MenuScanResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (!scanId) {
      // A rejected upload (incl. the 60s timeout) sets uploadError — surface it here and let the
      // user retry from this screen instead of silently bouncing to Home. Checked BEFORE the
      // in-flight guard because uploadingFromState stays true for the life of this navigation.
      if (uploadError) {
        setError(uploadError)
        return
      }
      // This page is opened immediately on scan submit while upload is in-flight.
      if (uploadingFromState || uploading) return
      errorLog('[MenuScanLoading] No scanId in state, redirecting to home')
      navigate(ROUTES.HOME, { replace: true })
      return
    }

    const poll = async (): Promise<void> => {
      try {
        const data = await dispatch(getMenuScanStatusThunk(scanId)).unwrap()
        setResult(data)
        if (
          data.status === MENU_SCAN_STATUS.COMPLETED ||
          data.status === MENU_SCAN_STATUS.FAILED
        ) {
          if (pollingRef.current) {
            clearInterval(pollingRef.current)
            pollingRef.current = null
          }
          navigate(ROUTES.MENU_SCAN_RESULTS, { state: { scanId, result: data }, replace: true })
        }
      } catch (e) {
        errorLog('[MenuScanLoading] Poll error', e)
        setError('Unable to load scan status.')
        if (pollingRef.current) {
          clearInterval(pollingRef.current)
          pollingRef.current = null
        }
      }
    }

    void poll()
    // eslint-disable-next-line no-void
    pollingRef.current = setInterval(() => void poll(), POLL_INTERVAL_MS)

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current)
    }
  }, [scanId, navigate, dispatch, uploadingFromState, uploading, uploadError])

  if (error) {
    return (
      <MenuScanLoadingScreen
        statusText="We couldn't scan that menu"
        secondaryText={`${error} You can head back and try another photo.`}
        hideSpinner={true}
        action={
          <Button
            variant="contained"
            onClick={() => navigate(ROUTES.HOME, { replace: true })}
            sx={{ textTransform: 'none', borderRadius: '10px', minHeight: '44px', px: 3 }}
          >
            Back to home
          </Button>
        }
      />
    )
  }

  const statusText = scanId
    ? result?.message ?? 'Processing your menu…'
    : 'Uploading your menu…'
  const secondaryText =
    scanId
      ? result?.status === MENU_SCAN_STATUS.OCR_DONE ||
        result?.status === MENU_SCAN_STATUS.EXTRACTION_DONE
        ? 'Identifying dishes and ingredients. This can take up to 30 seconds.'
        : 'Upload complete. Finding dishes… This can take up to 30 seconds.'
      : 'Preparing your menu… This can take up to 30 seconds.'

  return (
    <>
      <Helmet>
        <title>Resactly | Finding your dishes…</title>
      </Helmet>
      <MenuScanLoadingScreen
        statusText={statusText}
        secondaryText={secondaryText}
        isIndeterminate={true}
        partialDishes={result?.dishes ?? []}
      />
    </>
  )
}

export default MenuScanLoading
